import type { DealPromptAudienceSetMetrics } from '@openx/types/dealPrompt.ts';
import type {
  AudienceSegmentEntry,
  AudienceSegmentSetMetrics,
  InventoryBundle,
  InventoryDomain,
} from '@openx/types/gqlTypes.ts';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue.ts';
import type { TFunction } from 'i18next';
import {
  type AudienceSegmentSetMetricKey,
  type DealPromptContextType,
  dealPromptContextTypes,
  type MetricKey,
} from '../../utils/types.ts';
import type { DeviceDistributionItem } from './DealPromptAudienceDetailsMetricSection/DeviceDistribution.tsx';
import type { GatheredMetricsColumn } from './DealPromptAudienceDetailsMetricSection/GatheredMetrics.tsx';

type SearchableItem = InventoryDomain | InventoryBundle | AudienceSegmentEntry;
type FieldAccessor = (item: SearchableItem) => string | undefined;
type SimpleMetricKey = keyof Pick<DealPromptAudienceSetMetrics, 'reach' | 'avgCPM' | 'relevance'>;

const matchSearch = (field: string | undefined, search: string) =>
  field?.toLowerCase().includes(search.toLowerCase()) ?? false;

const domainFilters: FieldAccessor[] = [item => (item as InventoryDomain).domainName];

const bundleFilters: FieldAccessor[] = [item => (item as InventoryBundle).bundleId ?? undefined];

const audienceFilterKeys = [
  'name',
  'description',
  'segment_category',
  'segment_subcategory',
  'segment_type',
] as const satisfies ReadonlyArray<keyof AudienceSegmentEntry>;

type AudienceFilterKey = (typeof audienceFilterKeys)[number];

const audienceFilters: FieldAccessor[] = audienceFilterKeys.map(
  (key: AudienceFilterKey) => (item: SearchableItem) =>
    ((item as AudienceSegmentEntry)[key] ?? undefined) as string | undefined
);

export const getFilterPredicates = (contextType: DealPromptContextType): FieldAccessor[] => {
  switch (contextType) {
    case dealPromptContextTypes.DOMAINS:
      return domainFilters;
    case dealPromptContextTypes.APPS:
      return bundleFilters;
    case dealPromptContextTypes.AUDIENCE:
      return audienceFilters;
    default:
      return [];
  }
};

export const itemMatchesSearch = (
  item: SearchableItem,
  search: string,
  contextType: DealPromptContextType
): boolean => {
  return getFilterPredicates(contextType).some(accessor => matchSearch(accessor(item), search));
};

export const formatValue = (key: MetricKey, value: number) => {
  switch (key) {
    case 'totalBidSpendInUsd':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2 })}`;
    case 'avgBidCpm':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2 })}`;
    case 'relevance':
    case 'avgViewability':
    case 'avgOxWinRate':
    case 'avgPubWinRate':
      return (
        abbreviateNumericValue(value, { decimalPlaces: key === 'avgOxWinRate' || key === 'avgPubWinRate' ? 2 : 0 }) +
        '%'
      );
    case 'totalCprMarketRequests':
    case 'totalImpressions':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    default:
      return String(value);
  }
};

export const formatAudienceValue = (key: AudienceSegmentSetMetricKey, value: number) => {
  switch (key) {
    case 'clickThroughRate':
      return `${abbreviateNumericValue(value, { decimalPlaces: 2 })}%`;
    case 'videoCompletionRate':
      return `${abbreviateNumericValue(value, { decimalPlaces: 2 })}%`;
    case 'avgCPM':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2 })}`;
    case 'costPerUniqueReach':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2 })}`;
    case 'reach':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'relevance':
      return `${abbreviateNumericValue(value, { decimalPlaces: 2 })}%`;
    case 'totalRequests':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'activeIPs':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'cookies':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'devicesDesktop':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'devicesMobile':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'devicesTV':
      return abbreviateNumericValue(value, { decimalPlaces: 2 });
    case 'maxCPM':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2, allowZero: true })}`;
    case 'minCPM':
      return `$${abbreviateNumericValue(value, { decimalPlaces: 2, allowZero: true })}`;
    default:
      return String(value);
  }
};

export const getAudienceMetricsData = (metrics: AudienceSegmentSetMetrics, t: TFunction) => {
  const simpleCardLabels: Record<SimpleMetricKey, string> = {
    reach: t('Unique Users Reach'),
    avgCPM: t('Avg CPM'),
    relevance: t('Relevance'),
  };

  const simpleCardValues: Record<SimpleMetricKey, string> = {
    reach: formatAudienceValue('reach', metrics.reach) ?? t('N/A'),
    avgCPM: formatAudienceValue('avgCPM', metrics.avgCPM) ?? t('N/A'),
    relevance: formatAudienceValue('relevance', metrics.relevance) ?? t('N/A'),
  };

  const gatheredMetricsColumns: GatheredMetricsColumn[] = [
    {
      dataTest: 'audience-details-gathered-engagement',
      title: t('Engagement'),
      elements: [
        {
          dataTest: 'audience-details-gathered-clickThroughRate',
          title: t('Click Through Rate'),
          value: formatAudienceValue('clickThroughRate', metrics.clickThroughRate) ?? t('N/A'),
        },
        {
          dataTest: 'audience-details-gathered-videoCompletionRate',
          title: t('Video Completion Rate'),
          value: formatAudienceValue('videoCompletionRate', metrics.videoCompletionRate) ?? t('N/A'),
        },
      ],
    },
    {
      dataTest: 'audience-details-gathered-pricing',
      title: t('Pricing'),
      elements: [
        {
          dataTest: 'audience-details-gathered-cpmRange',
          title: t('CPM range'),
          value: `${formatAudienceValue('minCPM', metrics.minCPM) ?? t('N/A')} - ${
            formatAudienceValue('maxCPM', metrics.maxCPM) ?? t('N/A')
          }`,
        },
        {
          dataTest: 'audience-details-gathered-costPerUniqueReach',
          title: t('Cost Per Unique Reach'),
          value: formatAudienceValue('costPerUniqueReach', metrics.costPerUniqueReach) ?? t('N/A'),
        },
      ],
    },
    {
      dataTest: 'audience-details-gathered-scale',
      title: t('Scale'),
      elements: [
        {
          dataTest: 'audience-details-gathered-totalRequests',
          title: t('Total Requests'),
          value: formatAudienceValue('totalRequests', metrics.totalRequests) ?? t('N/A'),
        },
        {
          dataTest: 'audience-details-gathered-activeIPs',
          title: t('Active IPs'),
          value: formatAudienceValue('activeIPs', metrics.activeIPs) ?? t('N/A'),
        },
        {
          dataTest: 'audience-details-gathered-cookies',
          title: t('Cookies'),
          value: formatAudienceValue('cookies', metrics.cookies) ?? t('N/A'),
        },
      ],
    },
  ];

  const deviceDistributionItems: DeviceDistributionItem[] = [
    {
      dataTest: 'audience-details-device-mobile',
      icon: 'mobile',
      title: t('Mobile'),
      hint: t('(Smarphone + Tablet)'),
      value: formatAudienceValue('devicesMobile', metrics.devicesMobile) ?? t('N/A'),
    },
    {
      dataTest: 'audience-details-device-tv',
      icon: 'tv',
      title: t('TV Devices'),
      value: formatAudienceValue('devicesTV', metrics.devicesTV) ?? t('N/A'),
    },
    {
      dataTest: 'audience-details-device-desktop',
      icon: 'desktop',
      title: t('Desktop'),
      value: formatAudienceValue('devicesDesktop', metrics.devicesDesktop) ?? t('N/A'),
    },
  ];

  return {
    simpleCardLabels,
    simpleCardValues,
    gatheredMetricsColumns,
    deviceDistributionItems,
  };
};
